import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { maskIban, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, FileText, CheckCircle2 } from "lucide-react";

const KYC_PILL = {
  not_submitted: ["Not submitted", "bg-slate-100 text-slate-600"],
  under_review: ["Under review", "bg-blue-100 text-blue-700"],
  verified: ["Verified", "bg-emerald-100 text-emerald-700"],
  rejected: ["Rejected", "bg-red-100 text-red-700"],
};

export default function PaymentsVerification() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  useEffect(() => { api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  if (!user) return <div className="py-20 text-center text-slate-400">Loading…</div>;
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6" data-testid="payments-verification-page">
      <button onClick={() => navigate("/profile")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="payments-back"><ArrowLeft className="h-4 w-4" /> Back to profile</button>
      <h1 className="mb-1 font-heading text-2xl font-bold text-slate-900">Payments &amp; verification</h1>
      <p className="mb-6 text-sm text-slate-500">Add your UAE bank details and verify your Emirates ID so we can send your sales proceeds and deposit refunds.</p>
      <IdentityVerification user={user} />
      <BankDetails user={user} />
    </div>
  );
}

function FileField({ label, testid, file, onPick, onRemove, disabled }) {
  return (
    <div>
      <Label className="mb-1 block text-sm text-slate-600">{label}</Label>
      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2" data-testid={`${testid}-uploaded`}>
          {file.isPdf ? <div className="flex h-12 w-12 items-center justify-center rounded bg-white text-slate-500"><FileText className="h-6 w-6" /></div>
            : <img src={file.data} alt="" className="h-12 w-12 rounded object-cover" data-testid={`${testid}-preview`} />}
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Uploaded</p>
            <p className="truncate text-xs text-slate-500">{file.name}</p>
          </div>
          <label className="cursor-pointer text-xs font-semibold text-primary hover:underline" data-testid={`${testid}-replace`}>Replace<input type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" disabled={disabled} onChange={onPick} /></label>
          <button type="button" className="text-xs font-semibold text-red-600 hover:underline" data-testid={`${testid}-remove`} onClick={onRemove} disabled={disabled}>Remove</button>
        </div>
      ) : (
        <label className={`flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-primary"}`}>
          <input type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" data-testid={testid} disabled={disabled} onChange={onPick} />
          <span className="truncate text-slate-500">Choose file (JPG, PNG, PDF · max 5MB)</span>
        </label>
      )}
    </div>
  );
}

function IdentityVerification({ user }) {
  const { setUser } = useAuth();
  const status = user.kyc_status || "not_submitted";
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, cls] = KYC_PILL[status] || KYC_PILL.not_submitted;
  const locked = status === "under_review" || status === "verified";

  const [fileErr, setFileErr] = useState("");
  const pick = (setter) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(f.type)) { setFileErr(`"${f.name}" isn't a supported file type. Please upload a JPG, PNG or PDF.`); return; }
    if (f.size > 5 * 1024 * 1024) { setFileErr(`"${f.name}" is ${(f.size / 1048576).toFixed(1)}MB — files must be 5MB or smaller.`); return; }
    setFileErr("");
    const r = new FileReader();
    r.onload = () => setter({ name: f.name, data: r.result, isPdf: f.type === "application/pdf" });
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!front || !back) return;
    setBusy(true);
    try {
      await api.post("/kyc/submit", { front: front.data, back: back.data });
      const me = await api.get("/auth/me");
      setUser(me.data);
      setFront(null); setBack(null);
      toast.success("Submitted — we'll review it within 1 business day");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="kyc-section">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-slate-900">Identity Verification</h3>
        <span data-testid="kyc-status-pill" className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
      </div>
      {status !== "verified" && <p className="mt-1 text-sm text-slate-500">Required before your first payout. Upload a clear photo of your Emirates ID — front and back.</p>}

      {status === "rejected" && (
        <p data-testid="kyc-rejection-reason" className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">Rejected: {user.kyc_rejection_reason || "Please resubmit with a clear photo of your Emirates ID."}</p>
      )}

      {status === "verified" ? (
        <p className="mt-3 flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="kyc-verified-note"><ShieldCheck className="h-4 w-4" /> Your identity has been verified.</p>
      ) : status === "under_review" ? (
        <p className="mt-3 text-sm font-medium text-emerald-700" data-testid="kyc-under-review-note">Submitted{user.kyc_submitted_at ? ` ${fmtDateTime(user.kyc_submitted_at)}` : ""} — we'll review it within 1 business day</p>
      ) : (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <FileField label="Emirates ID — Front" testid="kyc-front" file={front} onPick={pick(setFront)} onRemove={() => setFront(null)} disabled={locked} />
            <FileField label="Emirates ID — Back" testid="kyc-back" file={back} onPick={pick(setBack)} onRemove={() => setBack(null)} disabled={locked} />
          </div>
          {fileErr && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="kyc-file-error">{fileErr}</p>}
          <Button data-testid="kyc-submit-btn" disabled={!front || !back || busy || locked} onClick={submit} className="mt-4 rounded-full bg-primary font-semibold hover:bg-primary/90 disabled:opacity-50">
            {busy ? "Submitting…" : "Submit for verification"}
          </Button>
        </>
      )}
    </div>
  );
}

function BankDetails({ user }) {
  const { setUser } = useAuth();
  const location = useLocation();
  const saved = user.bank_details;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ account_name: "", iban: "", bank_name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (saved) setF({ account_name: saved.account_name || "", iban: saved.iban || "", bank_name: saved.bank_name || "" }); }, [saved]);
  useEffect(() => { if (location.search.includes("bank=1") && !saved) setEditing(true); }, [location.search, saved]);
  const save = async () => {
    const iban = (f.iban || "").replace(/\s/g, "").toUpperCase();
    if (!f.account_name.trim() || !f.bank_name.trim() || !iban) { toast.error("Please fill all fields"); return; }
    if (!iban.startsWith("AE") || iban.length !== 23) { toast.error("Enter a valid UAE IBAN (starts with AE, 23 characters)"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { bank_details: { account_name: f.account_name.trim(), iban, bank_name: f.bank_name.trim() } });
      setUser(data); toast.success("Bank details saved successfully"); setEditing(false);
    } catch { toast.error("Could not save UAE bank details"); } finally { setBusy(false); }
  };
  return (
    <div id="bank" className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="bank-details-section">
      <h3 className="font-heading text-base font-bold text-slate-900">UAE bank details</h3>
      <p className="mt-0.5 text-xs text-slate-500">We need these to transfer your sales proceeds and deposit refunds directly to your account. The account holder name must match your verified Emirates ID.</p>
      {!editing && !saved && (
        <div className="mt-3"><Button data-testid="add-bank-btn" onClick={() => setEditing(true)} className="rounded-full bg-primary font-semibold">Add UAE bank details</Button></div>
      )}
      {!editing && saved && (
        <div className="mt-3 space-y-1 text-sm text-slate-700" data-testid="bank-details-saved">
          <p><span className="text-slate-400">Name:</span> {saved.account_name}</p>
          <p data-testid="bank-iban-masked"><span className="text-slate-400">IBAN:</span> <span className="font-mono">{maskIban(saved.iban)}</span></p>
          <p><span className="text-slate-400">Bank:</span> {saved.bank_name}</p>
          <Button variant="outline" size="sm" data-testid="edit-bank-btn" onClick={() => setEditing(true)} className="mt-2 rounded-full">Edit</Button>
        </div>
      )}
      {editing && (
        <div className="mt-3 space-y-3">
          <div><Label className="mb-1 block">Full name on account</Label><Input data-testid="bank-account-name" value={f.account_name} onChange={(e) => setF((p) => ({ ...p, account_name: e.target.value }))} className="rounded-lg" /></div>
          <div><Label className="mb-1 block">IBAN</Label><Input data-testid="bank-iban" value={f.iban} onChange={(e) => setF((p) => ({ ...p, iban: e.target.value }))} placeholder="AE07 0331 2345 6789 0123 456" className="rounded-lg" /></div>
          <div><Label className="mb-1 block">Bank name</Label><Input data-testid="bank-name" value={f.bank_name} onChange={(e) => setF((p) => ({ ...p, bank_name: e.target.value }))} className="rounded-lg" /></div>
          <Button data-testid="save-bank-btn" disabled={busy} onClick={save} className="rounded-full bg-primary font-semibold">Save UAE bank details</Button>
        </div>
      )}
    </div>
  );
}
